import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import { CurrencyInput } from '@eika/currency-input';
import { ErrorLabel } from '@eika/label';
import { ButtonToggles } from '@eika/radio-button';
import { Select } from '@eika/select';

import { changeSoknadField, openStep, verifySoknadField } from '../../../actions';
import { InputChangeEvent, NameValueStringEvent, StringKeyAnyValue, StringKeyValue } from '../../../actions/types';
import { AppState } from '../../../reducers';
import { ApplicationReducer } from '../../../reducers/application';
import { LoaderReducer } from '../../../reducers/loader';
import { trackEvent, TrackingStep } from '../../../util/piwik-utils';
import schemaGenerator from '../../../util/schemaGenerator';
import ValidationFields from '../../../util/validation/ValidationFields';
import Loading from '../../Messages/Loading';
import StepButtons from '../../Steps/StepButtons';

interface Props {
    action: {
        changeSoknadField: NameValueStringEvent;
        verifySoknadField: NameValueStringEvent;
        openStep: (step: number) => void;
    };
    onAboutYouNextClick: () => void;
    application: ApplicationReducer;
    loader: LoaderReducer;
    text: StringKeyValue;
}

const createSelectOptions = (options: StringKeyAnyValue[]) => {
    return (
        <>
            <option disabled>Velg et alternativ</option>
            {options.map((af: any) => {
                return (
                    <option key={af.value} value={af.value}>
                        {af.text}
                    </option>
                );
            })}
        </>
    );
};

class About extends Component<Props> {
    componentDidMount() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    onBlur = (e: InputChangeEvent) => {
        const { name, value } = e.target;
        setTimeout(() => this.props.action.verifySoknadField(name, value), 250);
    };

    onChange = (e: any) => {
        this.props.action.changeSoknadField(e.target.name, e.target.value);
    };

    onChangeButtonGroup = (field: string, value: string | number) => {
        this.props.action.changeSoknadField(field, String(value));
    };

    render() {
        const { application, text, loader, onAboutYouNextClick, action } = this.props;
        const { errors } = application;
        const { isFetchingKdk } = loader;

        if (isFetchingKdk) return <Loading title={text.loadingDefault} />;

        return (
            <div className="about">
                <div />
                <h2 className="credit-card-application-form-title">{text.aboutTitle}</h2>
                <div className="credit-card-application-input">
                    <Select
                        id="sel"
                        onChange={(e) => this.onChangeButtonGroup(ValidationFields.SIVILSTATUS, e.target.value)}
                        value={application.sivilstatus}
                        label={text.formMaritalStatusLabel}
                    >
                        {createSelectOptions(schemaGenerator(text.schemaFieldMaritalStatusArray))}
                    </Select>
                </div>
                <div className="credit-card-application-input">
                    <Select
                        id="sel"
                        onChange={(e) => this.onChangeButtonGroup(ValidationFields.BOFORHOLD, e.target.value)}
                        value={application.boforhold}
                        label={text.formLivingConditionsLabel}
                    >
                        {createSelectOptions(schemaGenerator(text.schemaFieldHousingArray))}
                    </Select>
                </div>
                <div className="credit-card-application-input">
                    <Select
                        id="sel"
                        onChange={(e) => this.onChangeButtonGroup(ValidationFields.ARBEIDSFORHOLD, e.target.value)}
                        value={application.arbeidsforhold}
                        label={text.formWorkingConditionsLabel}
                    >
                        {createSelectOptions(schemaGenerator(text.schemaFieldEmploymentArray))}
                    </Select>
                </div>
                <div className="credit-card-application-input">
                    <CurrencyInput
                        name={ValidationFields.SIKRET_GJELD}
                        value={application.annensikretgjeld}
                        error={errors.annensikretgjeld}
                        onChange={this.onChange}
                        // @ts-ignore
                        onBlur={this.onBlur}
                        label={text.formSecuredDebtLabel}
                        sublabel={text.formSecuredDebtSubLabel}
                        placeholder={text.currencyInputPlaceholder}
                        type="tel"
                    />
                </div>
                <div className="credit-card-application-input">
                    <ButtonToggles
                        label={text.formHasOtherCardsLabel}
                        onChange={(e) => this.onChangeButtonGroup(ValidationFields.ANDREKORT, e)}
                        selectedOption={application.harAndreKort}
                        options={[
                            { label: 'Nei', value: '0' },
                            { label: 'Ja', value: '1' },
                        ]}
                    />
                    {errors.harAndreKort && (
                        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                            <ErrorLabel text={errors.harAndreKort} />
                        </div>
                    )}
                </div>
                {errors.formError && (
                    <div className="text-center" style={{ marginTop: '40px' }}>
                        <ErrorLabel text={errors.formError} />
                    </div>
                )}
                <StepButtons
                    showPrevious
                    showNext
                    previousChangeEvent={() => action.openStep(2)}
                    nextChangeEvent={() => {
                        trackEvent(TrackingStep.About);
                        onAboutYouNextClick();
                    }}
                />
            </div>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    text: state.context.config.data.text,
    application: state.application,
    loader: state.loader,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    action: bindActionCreators(
        {
            verifySoknadField,
            changeSoknadField,
            openStep,
        },
        dispatch,
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(About);
